import axios from 'axios';
import { AUTH_URL } from '../config/apiUrls';
import type {
  Connection,
  ConnectionsOverview,
  CreateConnectionPayload,
  PresenceStatus,
} from '../types/connections';

const connectionsClient = axios.create({
  baseURL: AUTH_URL,
  headers: { 'Content-Type': 'application/json' },
});

connectionsClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

connectionsClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  },
);

export const connectionsApi = {
  getOverview: async (): Promise<ConnectionsOverview> => {
    const response = await connectionsClient.get<ConnectionsOverview>('/connections');
    return response.data;
  },

  create: async (payload: CreateConnectionPayload): Promise<Connection> => {
    const response = await connectionsClient.post<Connection>('/connections', payload);
    return response.data;
  },

  accept: async (connectionId: number): Promise<Connection> => {
    const response = await connectionsClient.post<Connection>(
      `/connections/${connectionId}/accept`,
    );
    return response.data;
  },

  reject: async (connectionId: number): Promise<Connection> => {
    const response = await connectionsClient.post<Connection>(
      `/connections/${connectionId}/reject`,
    );
    return response.data;
  },

  remove: (connectionId: number) =>
    connectionsClient.delete(`/connections/${connectionId}`),

  getPresence: async (): Promise<PresenceStatus> => {
    const response = await connectionsClient.get<PresenceStatus>('/presence');
    return response.data;
  },

  setPresence: async (active: boolean): Promise<PresenceStatus> => {
    const response = await connectionsClient.put<PresenceStatus>('/presence', { active });
    return response.data;
  },
};
